import { useLocation } from '@gatsbyjs/reach-router';
import { navigate } from 'gatsby';
import React, { useEffect } from 'react';

import { Curation } from '@components/DrilldownHeader';
import RelatedRecordsTable from '@components/RelatedRecordsTable';
import YourNotes from '@components/YourNotes';

import { useCurationMode } from '@contexts/CurationModeContext';

import { useCuration } from '@hooks/useCuration';
import { useViewable } from '@hooks/useViewable';

import AppLayout from '@layouts/AppLayout';
import {
  FlexColumn,
  FlexColumnContainer,
  MainContainer,
  PageBody,
} from '@layouts/DrilldownStyle';

import type { FhirResource } from '@models/fhir-resource';

import type { Annotation, ResourceAssociationUnion } from 'generated/types';

function DrilldownWrapper({
  loading = true,
  title: initTitle,
  backRoute = '',
  selfReported = false,
  children,
  curation = true,
  notes = [],
  associatedRecords = [],
  resource,
  ...props
}: Curation.DrilldownHeaderProps & {
  // backRoute: string;
  // loading?: boolean;
  selfReported?: boolean;
  // title?: string | null;
  curation?: boolean;
  notes?: Annotation[];
  associatedRecords?: ResourceAssociationUnion[];
  resource: FhirResource | null | undefined;
}) {
  // Use context for curation mode (provided by DynamicResourceForms when in PDF curation flow)
  const { curationMode } = useCurationMode();
  useViewable(resource ? [resource] : undefined);
  const {
    state: { isCreating, isEditing },
    startCurating,
  } = useCuration();
  const { pathname } = useLocation();

  const id = resource?.id;

  // Ensure record exists
  useEffect(() => {
    if (!loading && !id && isEditing && typeof window !== 'undefined') {
      navigate(backRoute);
    }
    if (curation) {
      startCurating(resource ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, backRoute, id, isEditing]);

  const title = React.useMemo(
    () => (typeof initTitle === 'string' ? initTitle : null),
    [initTitle]
  );

  // Curation mode: simplified layout without nested AppLayout
  if (curationMode) {
    return (
      <div className="curation-mode-wrapper">
        <Curation.DrilldownHeader
          backRoute={undefined}
          hasNotes={false}
          selfReported={selfReported}
          title={title}
          resource={resource}
          {...props}
        />
        <PageBody padding="16px 20px" style={{ marginBottom: 0 }}>
          {children}
        </PageBody>
      </div>
    );
  }

  return (
    <AppLayout friendlyName={title} loading={loading}>
      <Curation.DrilldownHeader
        backRoute={backRoute}
        hasNotes={false}
        selfReported={selfReported}
        title={title}
        resource={resource}
        {...props}
      />
      <MainContainer maxWidth="1200px">
        <FlexColumnContainer>
          <FlexColumn>
            {children ? <PageBody>{children}</PageBody> : null}
            {pathname.includes('insurance') ||
            pathname.includes('vital-signs') ||
            pathname.includes('body') ||
            isCreating ? null : (
              <RelatedRecordsTable records={associatedRecords} />
            )}
          </FlexColumn>

          <YourNotes notes={notes} />
        </FlexColumnContainer>
      </MainContainer>
    </AppLayout>
  );
}

export default DrilldownWrapper;
